import { cloneDeep } from 'lodash';
import { useCallback, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { ContextType } from 'api/conversations';
import { ProjectGlobalConversation } from 'api/project-conversations';
import { ConversationContext } from 'data/conversation';
import { useGlobalConversationLastSeenMutation } from './project-global-conversation';
import {
  globalConversationQueryKey,
  GlobalConversationQueryResult,
} from './utils';

const useAlreadyPushedCommentId = (): ((
  context: ConversationContext,
  lastSeenCommentCreatedAt: number,
) => boolean) => {
  const alreadySeenCommentIds = useRef<
    Map<`${ConversationContext['id']} ${ConversationContext['type']}`, number[]>
  >(new Map());

  return useCallback(
    (
      context: ConversationContext,
      lastSeenCommentCreatedAt: number,
    ): boolean => {
      const lastSeenCommentCreatedAts = alreadySeenCommentIds.current.get(
        `${context.id} ${context.type}`,
      );

      if (lastSeenCommentCreatedAts == null) {
        alreadySeenCommentIds.current.set(`${context.id} ${context.type}`, [
          lastSeenCommentCreatedAt,
        ]);

        return false;
      }
      if (lastSeenCommentCreatedAts.includes(lastSeenCommentCreatedAt)) {
        return true;
      }
      alreadySeenCommentIds.current.set(
        `${context.id} ${context.type}`,
        lastSeenCommentCreatedAts.concat(lastSeenCommentCreatedAt),
      );

      return false;
    },
    [],
  );
};

export const useResetUnreadCount = (
  projectId: number,
): ((
  context: ConversationContext,
  lastSeenCommentCreatedAt: number,
) => void) => {
  const queryClient = useQueryClient();
  const { mutate: mutateMembersLastSeen } =
    useGlobalConversationLastSeenMutation();
  const upsertAlreadySeen = useAlreadyPushedCommentId();

  return useCallback(
    (context: ConversationContext, lastSeenCommentCreatedAt: number) => {
      if (upsertAlreadySeen(context, lastSeenCommentCreatedAt)) {
        return;
      }
      const queryKey = globalConversationQueryKey.byProjectId(projectId);
      const queryResult =
        queryClient.getQueryData<GlobalConversationQueryResult>(queryKey);

      if (!queryResult) {
        return;
      }
      const predicateByCurrentConversation = (
        current: ProjectGlobalConversation,
      ) =>
        context.id === current.entityId && context.type === current.contextType;

      const currentConversation = queryResult.conversations.find(
        predicateByCurrentConversation,
      );

      if (!currentConversation) {
        return;
      }
      mutateMembersLastSeen({
        data: { lastSeenCommentCreatedAt },
        entityId: context.id,
        entityType: ContextType[context.type],
      });
      const updatedConversation = cloneDeep(currentConversation);

      updatedConversation.unreadMessages = 0;
      const updatedConversations = queryResult.conversations.map((c) =>
        predicateByCurrentConversation(c) ? updatedConversation : c,
      );

      queryClient.setQueryData<GlobalConversationQueryResult>(queryKey, {
        conversations: updatedConversations,
        unread: queryResult.unread - currentConversation.unreadMessages,
      });
    },
    [upsertAlreadySeen, mutateMembersLastSeen, projectId, queryClient],
  );
};
